import React from "react";
import { graphql, useRefetchableFragment } from "react-relay";
import { useHistory } from "react-router-dom";

import { noop } from "lodash";

import growthbook from "~/lib/ab/growthbook";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { inCalendarMode, inVideocallMode, isDesktop, isDesktopApp, isWeb } from "~/lib/utils";
import { LIBRARY_PAGE_PATH, SHARED_WITH_ME_PAGE_PATH } from "~/paths";

import { AI_PATH } from "./aiPage";
import { CONNECT_PATH } from "./ConnectPage";
import { DESKTOP_LAUNCH_PATH } from "./DesktopLaunchPage";
import { DESKTOP_PATH } from "./DesktopPage";
import { INTEGRATE_PATH } from "./IntegratePage";
import { INVITE_PATH } from "./InvitePage";
import { RECORD_MEETING_PATH } from "./ManualRecordSelectPage";

import type { JourneyContext_viewer$key as ViewerKey } from "~/__generated__/JourneyContext_viewer.graphql";
import type {
	JourneyContextQuery$data as QueryResponse,
	JourneyContextQuery as QueryType,
} from "~/__generated__/JourneyContextQuery.graphql";
import type { JourneyContextViewerQuery } from "~/__generated__/JourneyContextViewerQuery.graphql";

type JourneyContextType = {
	journey: string[];
	nextStep: (input?: { source: string; val: string }) => void;
	startJourney: () => void;
	goToFinalStep: () => void;
	user: QueryResponse["user"] | null;
};

export const JourneyContext = React.createContext<JourneyContextType>({
	journey: [],
	nextStep: noop,
	startJourney: noop,
	goToFinalStep: noop,
	user: null,
});
JourneyContext.displayName = "OnboardingJourneyContext";

export const JourneyContextProvider: React.FC = ({ children }) => {
	const history = useHistory();

	const {
		viewer: viewerKey,
		user,
		account,
	} = useLazyLoadQuery<QueryType>(
		graphql`
			query JourneyContextQuery {
				viewer {
					...JourneyContext_viewer
				}
				user {
					id
					internalAttributes {
						primaryConferenceCallProvider
						manager {
							id
						}
					}
				}
				account {
					allowInviting
					ssoProvider
					hrisIntegrated
					isPersonalType
				}
			}
		`,
		{},
		{ fetchPolicy: "store-and-network" },
	);

	const [viewer, refetch] = useRefetchableFragment<JourneyContextViewerQuery, ViewerKey>(
		graphql`
			fragment JourneyContext_viewer on Viewer @refetchable(queryName: "JourneyContextViewerQuery") {
				hasAccessToCopilot
				questionAnswers

				signupConsumedInvite {
					id
					path
				}
				signupConsumedStreamInviteStream {
					url
				}
				signupMeetingStreams {
					id
					url
					createdAt
					nextEventWithContent {
						url
					}
				}
				signupOneononeStreams {
					url
				}
				signupPersonalStreams {
					url
				}
				signupTemplate {
					id
					url
					creator {
						isViewer
					}
				}
				signupTemplateStream {
					id
					url
				}
				signupMeetingRecording {
					id
				}
				integrations {
					google {
						id
					}
				}
			}
		`,
		viewerKey,
	);

	const journey = React.useMemo(() => {
		const steps = [];

		if (account.ssoProvider) steps.push(CONNECT_PATH);

		if (viewer.hasAccessToCopilot) {
			// The /o/record-meeting/ step might be skipped depending on the result of the user input in /o/ai/.
			// Ideally we would have some better data structure to represent this than an array (like a tree with multiple paths),
			// but for now the easiest solution is to append the record-meeting step and then check later if we should skip or not.
			steps.push(AI_PATH);
			steps.push(RECORD_MEETING_PATH);
		}

		const showIntegrate = isDesktop && isWeb && !isDesktopApp && !inVideocallMode && !inCalendarMode;

		// New onboarding flow: INVITE_PATH comes before INTEGRATE_PATH
		if (account.allowInviting) steps.push(INVITE_PATH);
		if (showIntegrate) {
			steps.push(INTEGRATE_PATH);
			steps.push(DESKTOP_PATH);
			steps.push(DESKTOP_LAUNCH_PATH);
		}

		return steps;
	}, [account, viewer]);

	const runFinalStep = React.useRef(false);

	const finalStep = React.useCallback(() => {
		const {
			signupConsumedInvite,
			signupConsumedStreamInviteStream,
			signupMeetingStreams: meetings,
			signupTemplate,
			signupTemplateStream,
			signupMeetingRecording,
		} = viewer;

		try {
			let invitePath;
			if (signupConsumedStreamInviteStream?.url)
				invitePath = new URL(signupConsumedStreamInviteStream.url, String(window.location));
			else invitePath = new URL(signupConsumedInvite?.path ?? "invalid url");
			history.push(invitePath.pathname);
		} catch {
			if (signupTemplate?.url && signupTemplate?.creator?.isViewer) {
				// Only redirect to the template if it's the user is the creator of the template
				// This is just to determine if the user was signed up with edited template content
				history.push(signupTemplate.url, { fromOnboarding: true });
			} else if (signupTemplateStream?.url) {
				history.push(signupTemplateStream?.url, { fromOnboarding: true });
			} else if (signupTemplate?.url) {
				history.push(signupTemplate.url, { fromOnboarding: true });
			} else if (signupMeetingRecording?.id) {
				history.push(LIBRARY_PAGE_PATH, { fromOnboarding: true, recordingId: signupMeetingRecording.id });
			} else if (meetings?.[0]?.nextEventWithContent) {
				history.push(meetings?.[0]?.nextEventWithContent?.url, { fromOnboarding: true });
			} else if (growthbook.isOn("demo-meeting-recording")) {
				history.push(SHARED_WITH_ME_PAGE_PATH, { fromOnboarding: true });
			} else {
				history.push(meetings?.[0]?.url ?? "/meetings/", { fromOnboarding: true });
			}
		}
	}, [history, viewer]);

	if (runFinalStep.current) {
		finalStep();
	}

	const goToFinalStep = React.useCallback(() => {
		refetch({}, { fetchPolicy: "network-only", onComplete: () => (runFinalStep.current = true) });
	}, [refetch]);

	const nextStep = React.useCallback(
		(input?: { source?: string; val?: string }) => {
			let nextStepIndex = journey.indexOf(window.location.pathname) + 1;

			// Handle AI flow logic
			if (input?.source === AI_PATH) {
				if (input.val === "all" || input.val === "organized") {
					// Skip the record-meeting step for automatic recording options
					if (journey[nextStepIndex] === RECORD_MEETING_PATH) {
						nextStepIndex++;
					}
				}
				// If user selected "off" (manual), show the record-meeting page - no need for additional logic
			}

			// Users who see the browser extension step on IntegratePage will also see the DesktopPage
			// Users who see the desktop download step on IntegratePage need to skip the DesktopPage
			if (input?.source === INTEGRATE_PATH && input?.val === "desktop-downloaded") {
				nextStepIndex++;
			}

			if (nextStepIndex >= journey.length) {
				goToFinalStep();
			} else {
				history.push(journey[nextStepIndex]);
			}
		},
		[history, journey, goToFinalStep],
	);

	const startJourney = React.useCallback(() => {
		if (journey.length > 0) {
			history.push(journey[0]);
		}
	}, [journey, history]);

	const value = React.useMemo(
		() => ({
			journey,
			nextStep,
			startJourney,
			goToFinalStep,
			user,
		}),
		[journey, nextStep, startJourney, goToFinalStep, user],
	);

	return <JourneyContext.Provider value={value}>{children}</JourneyContext.Provider>;
};
